import type { COMPANY_TYPES } from 'definitions';
import type { BillingInterval, BillingPlan } from 'definitions/billing-plans';

type CompanyType = (typeof COMPANY_TYPES)[number];

/** Carried wherever the account type is known, so each report can be split into clients and agencies. */
export type UmamiAccountType = 'client' | 'agency';

type Type = { type: UmamiAccountType };
type SigninMethod = { method: 'password' | 'passkey' };

/**
 * Every event the dashboard sends, and the data each one carries. The names are
 * load-bearing: saved Umami reports (funnels, attribution) select on them, and a renamed
 * one leaves those reports silently empty. Data is flat strings of 50 characters or fewer,
 * enums wherever possible, and never anything that identifies a person.
 *
 * docs/frontend/funnel-analytics.md has the same list with where each one fires.
 */
export type UmamiEvents = {
  // Sign in
  'signin-attempted': SigninMethod;
  'signin-succeeded': SigninMethod;
  'signin-failed': SigninMethod & {
    reason: 'wrong-credentials' | 'no-email' | 'passkey-cancelled' | 'passkey-rejected' | 'error';
  };

  // Forgotten password: the request, then the link from the email
  'password-reset-viewed': undefined;
  'password-reset-requested': undefined;
  'password-reset-request-failed': undefined;
  'password-reset-link-opened': undefined;
  'password-reset-completed': undefined;
  'password-reset-failed': { reason: 'no-code' | 'rejected' };

  // An invited teammate setting their password
  'invitation-opened': undefined;
  'invitation-accepted': undefined;
  'invitation-failed': undefined;

  // The signup chat, then the email confirmation
  'signup-opened': undefined;
  'signup-question-asked': { question: string };
  'signup-started': undefined;
  'signup-details-submitted': undefined;
  'signup-details-invalid': { fields: string };
  'signup-completed': Type;
  'signup-failed': Type;
  // Not `sending`: the panel cannot be closed while the account is being created.
  'signup-closed': { stage: 'pitch' | 'intro' | 'form' | 'type' | 'done' };
  'email-verified': Type;
  'email-verify-failed': undefined;

  // The product updates panel beside the sign-in form
  'updates-question-asked': { announcement: string; question: string };
  'updates-more-clicked': { announcement: string };
  'updates-link-clicked': { announcement: string; link: 'updates-page' | 'reply' };

  // Onboarding, signed in
  'checkout-started': Type & { plan: BillingPlan | 'agency-fee'; interval: BillingInterval };
  'trial-started': Type;
  'website-added': Type;
  'snippet-copied': Type & { method: 'paste' | 'gtm' };
  'onboarding-completed': Type;
};

export type UmamiEvent = keyof UmamiEvents;

/**
 * Keyed by COMPANY_TYPES, so renaming or adding a company type fails the type check here
 * instead of that type silently dropping out of the reports.
 */
export const UMAMI_ACCOUNT_TYPES: Readonly<Record<CompanyType, UmamiAccountType>> = {
  'Client Account': 'client',
  Agency: 'agency',
};

/** For a value from the network (verify's `accountType`). Anything that is not a company type is not reported. */
export const umamiAccountType = (companyType: unknown): UmamiAccountType | null =>
  typeof companyType === 'string' && Object.hasOwn(UMAMI_ACCOUNT_TYPES, companyType)
    ? UMAMI_ACCOUNT_TYPES[companyType as CompanyType]
    : null;

export const accountTypeOf = (isAgency: boolean): UmamiAccountType => (isAgency ? 'agency' : 'client');

/** Free text (a question's wording) cut to the 50 characters an event value may have. */
export const umamiLabel = (text: string): string => text.trim().slice(0, 50);
